import { observer } from "mobx-react"; // Importing observer from mobx-react for state management
import React from "react"; // Importing React
import { IOption } from "../../../../models/ICommon"; // Importing IOption interface
import { formatMessage } from "../../../../translations/format-message";
import DraggableEvent from "../draggableEvent"; // Importing DraggableEvent component
import AddBreakDetailsPopupHOC from "../hoc/add-break-details-popup-hoc"; // Importing HOC for add break details popup
import AllMoreEventsPopupHOC from "../hoc/all-more-events-popup-hoc";
import ReservationDetailsPopupHOC from "../hoc/reservation-details-popup-hoc"; // Importing HOC for reservation details popup
import AddBreakDetailsComponent from "../modal-components/add-break-details-component"; // Importing AddBreakDetailsComponent
import AllMoreEventsComponent from "../modal-components/all-more-events-component";
import StaffBreakCSSProperties from "./staff-break-css-properties";

interface IProps {
  day: any; // Day information
  currentDate: any; // Current date
  dataObject: any; // Data object for the event
  timeLabel: any; // Time label for the event
  start: any; // Start time of the event
  end: any; // End time of the event
  addBreakDetailsPopup: boolean; // Flag to toggle break details popup
  overlappingCount: any; // Count of overlapping events
  defaultMargin: any; // Default margin for event styling
  allMatchedEvents: any[]; // List of all matched events
  allMatchedBreaks?: any[]; // List of all matched breaks
  cellWidth: any; // Width of a single cell
  staffList: IOption[]; // List of staff options
  storeId: number; // Store ID
  addBreakDetailsToggleHandler: () => void; // Handler to toggle break details popup
  viewOnly: boolean; // Flag for view-only mode
  breaksCount?: number; // Optional count of breaks
  changeView?: (view: string) => void; // Optional function to change view
  showAllMoreEventsPopup: boolean; // Flag to show "All More Events" popup
}

const BreakEventComponent = (props: any) => {
  const {
    day, // Destructuring day
    currentDate, // Destructuring currentDate
    dataObject, // Destructuring dataObject
    timeLabel, // Destructuring timeLabel
    start, // Destructuring start
    end, // Destructuring end
    overlappingCount, // Destructuring overlappingCount
    defaultMargin, // Destructuring defaultMargin
    allMatchedEvents, // Destructuring allMatchedEvents
    cellWidth, // Destructuring cellWidth
    staffList, // Destructuring staffList
    storeId,
    viewOnly, // for viewOnly mode
    breaksCount, // Optional count of breaks
    changeView, // Optional function to change view
    showAllMoreEventsPopup, // Flag to show "All More Events" popup
  } = props;

  const shouldShowBreaks = props.breaksCount > 0 ? true : false; // Determine if breaks should be shown

  // Handler to open "All More Events" popup
  const openAllMoreEventsPopup = () => {
    props.allMoreEventsToggleHandler(); // Toggle "All More Events" popup
  };

  return (
    <>
      <React.Fragment>
        {/* Render the AddBreakDetailsComponent if the popup is active */}

        {props.allMoreEventsPopup && (
          <AllMoreEventsComponent
            onModalClosed={props.allMoreEventsToggleHandler} // Close handler for popup
            matchedEvent={dataObject} // Matched event data
            // AllEvents={allEvents} // List of all events
            // addBreakDetailsToggleHandler={openAddBreakDetailsPopup} // Pass add break details handler
            // openReservationToggleHandler={handleEventClick} // Flag for showing reservation details popup
            viewOnly={viewOnly} // Flag for view-only mode
            storeId={storeId} // Store ID
            day={day} // Day information
            staffList={staffList} // List of staff options
            changeView={changeView} // Optional function to change view
            breaks={props.allMatchedBreaks} // List of all breaks
          />
        )}

        {props.addBreakDetailsPopup && (
          <AddBreakDetailsComponent
            id={dataObject?.BreakID} // Passing BreakID to AddBreakDetailsComponent
            staffId={dataObject?.StaffId} // Passing staffId to AddBreakDetailsComponent
            staffList={staffList} // Passing staffList to AddBreakDetailsComponent
            startTime={dataObject?.StartTime} // Passing startTime to AddBreakDetailsComponent
            onModalClosed={props.addBreakDetailsToggleHandler} // Passing toggle handler to AddBreakDetailsComponent
            storeId={props.storeId}
            viewOnly={props.viewOnly} // Passing viewOnly for view only mode
          />
        )}

        <DraggableEvent
          key={`${day}-${timeLabel}`} // Unique key for DraggableEvent
          currentDate={currentDate} // Passing currentDate to DraggableEvent
          area="Inner" // Setting area as "Inner"
          data={dataObject} // Passing dataObject to DraggableEvent
          style={StaffBreakCSSProperties(
            start, // Start time for styling
            end, // End time for styling
            overlappingCount * defaultMargin, // Calculating margin for overlapping events
            cellWidth * (dataObject?.TotalTime / 5) // Calculating width based on total time
          )}
        >
          {({ onResizingChange }) => (
            <React.Fragment>
              <div
                className="event break"
                onClick={props.addBreakDetailsToggleHandler}
              >
                {/* Displaying event title */}
                {dataObject?.Title == "Break"
                  ? formatMessage("break")
                  : dataObject?.Title}
                <br />
                {/* Displaying event start and end time */}
                {dataObject?.StartTime} - {dataObject.EndTime}
              </div>
              {(showAllMoreEventsPopup || dataObject?.IsMoreEvents) && (
                <div className="row">
                  {/* Render "More Events" link if applicable */}

                  <React.Fragment>
                    <div
                      className="col-lg-4 more-events"
                      style={{ cursor: "pointer" }} // Pointer cursor style
                      onClick={openAllMoreEventsPopup} // Click handler to open "All More Events" popup
                    >
                      +{" "}
                      {shouldShowBreaks && (
                        <span className="">
                          {breaksCount}{" "}
                          {breaksCount > 1 ? (
                            <span>{formatMessage("breaks")}</span>
                          ) : (
                            <span>{formatMessage("break")}</span>
                          )}
                        </span>
                      )}
                      {/* Display count of additional events */}
                    </div>
                  </React.Fragment>
                </div>
              )}
            </React.Fragment>
          )}
        </DraggableEvent>
      </React.Fragment>
    </>
  );
};

export default AllMoreEventsPopupHOC(
  AddBreakDetailsPopupHOC(
    ReservationDetailsPopupHOC(observer(BreakEventComponent))
  ) // Wrapping component with HOCs and observer
);
